/**
 * Resolve / unresolve a comment (#2246).
 *
 * Backed by `POST .../comments/{id}/resolve` and
 * `POST .../comments/{id}/unresolve`. Both endpoints are idempotent
 * server-side (see the api-scriptix implementation) — hitting resolve
 * on an already-resolved comment keeps the original `resolved_at` /
 * `resolved_by_id` values, and unresolve is a no-op on an already-open
 * comment. The panel can therefore treat the mutation as safe to
 * retry.
 */

import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { Comment } from '@utils/types/comments/comments.ts';

interface ResolveParams {
  session_id: string;
  document_id: string;
  comment_id: string;
}

function endpoint(
  session_id: string,
  document_id: string,
  comment_id: string,
  action: 'resolve' | 'unresolve'
): string {
  const base = POST_URL(session_id, document_id).createComment;
  return `${base}/${comment_id}/${action}`;
}

async function callAction(
  { session_id, document_id, comment_id }: ResolveParams,
  action: 'resolve' | 'unresolve'
): Promise<Comment> {
  const response = await Axios.post<Comment>(
    endpoint(session_id, document_id, comment_id, action)
  );
  return response.data;
}

export function useResolveComment() {
  const queryClient = useQueryClient();
  return useMutation((params: ResolveParams) => callAction(params, 'resolve'), {
    onSuccess: () => {
      queryClient.invalidateQueries(['comments']);
    }
  });
}

export function useUnresolveComment() {
  const queryClient = useQueryClient();
  return useMutation(
    (params: ResolveParams) => callAction(params, 'unresolve'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['comments']);
      }
    }
  );
}
